import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function KentCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Kent Pothole Claims | 39% of Unclassified Roads RED | Fixtyer"
        description="Kent County Council admits a £625m road maintenance backlog and filled 233,509 potholes in five years — while RED-rated unclassified roads more than doubled to 39% in a single year. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/kent"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Kent', url: '/councils/kent' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Kent has a GREEN spend scorecard — can I still claim?',
            answer: 'Yes. Kent spends well beyond its DfT allocation — £79.3m of capital against a £54.3m allocation in 2025/26 — but the DfT still rates its road condition AMBER, and every road class in Kent has declined since 2020. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on how much the council spends in aggregate. A big budget spread across an 8,811km network does not fix the pothole you hit.',
          },
          {
            question: 'What does the jump to 39% RED unclassified roads mean for my claim?',
            answer: "Unclassified roads make up 5,484km — 62% of Kent's network — and the council's own data shows the RED share more than doubled in a single year, from 18% in 2023 to 39% in 2024. That is roughly 2,100km of residential streets, estate roads and rural lanes the council itself classes as needing consideration for maintenance. If your pothole was on one of them, the council's own published figures put it on notice that this part of its network is in widespread poor condition.",
          },
          {
            question: 'How often was my road actually surveyed?',
            answer: "It depends on the class. Kent SCANNER-surveys all A and B roads every year, but C-roads only over a two-year cycle (50% each year). On unclassified roads — most of the network — only those classed as 'Locally Important' get SCANNER surveys over two years; the rest receive a coarse visual inspection (CVI) over a two-year period. For most residential roads, condition data could be up to two years old and based on a visual drive-by, not laser measurement.",
          },
          {
            question: "Does Kent's £625m backlog admission help my claim?",
            answer: "Yes, in two ways. First, it is documented knowledge: Kent's own transparency report states the road maintenance backlog was estimated at circa £625m in 2023/24, with an annual funding shortfall of over £30m just to keep roads in a steady state. The council cannot claim to be surprised its network is producing defects. Second, lack of money is not a Section 58 defence — the statutory test is whether the council took reasonable care to secure the road was not dangerous, and courts do not excuse dangerous defects on budget grounds.",
          },
          {
            question: "Does Kent's £14.2m Pothole Recovery Strategy affect my claim?",
            answer: 'It can help it. The council says the strategy is "intended to address the significant damage to our roads caused by winter weather in recent years" — a written admission that recent winters caused significant, known damage across the network. Knowledge of accelerated deterioration raises the bar for what a reasonable inspection and repair regime looks like in the affected period. If your incident falls in that window, the council\'s own framing works in your favour.',
          },
          {
            question: 'How many potholes does Kent actually fill?',
            answer: 'A lot — and consistently. Kent filled 44,939 potholes in 2024/25 alone and 233,509 over the five years to 2024/25, an average of roughly 128 every day. Public pothole reports nearly doubled from 13,392 in 2021/22 to 32,146 in 2023/24. A network producing defects at that rate is one where potholes routinely form between inspections — exactly the scenario where prior reports and photographic evidence decide claims.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="Kent County Council" 
        councilSlug="kent" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/kent"
        headline="Kent Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Kent</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Kent: A £625m Backlog and Unclassified Roads in Freefall
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Kent County Council runs England's gateway network — 8,811km of road — and spends well 
            beyond its DfT allocation, earning a GREEN spend scorecard. Yet the overall rating is 
            <strong className="text-amber-400"> AMBER</strong> — because every road class has declined 
            since 2020, the council admits a <strong className="text-rose-400">£625m road maintenance 
            backlog</strong>, and RED-rated unclassified roads more than doubled to 39% in a single year.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">39%</div>
            <div className="text-white font-medium mt-2">Of unclassified roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 18% the year before. Unclassified roads are 5,484km — 62% of Kent's network — 
              so that's roughly 2,100km of residential streets and rural lanes the council's own data 
              says should be considered for maintenance.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Kent's own transparency report — and unlike most AMBER 
            councils, there is no bright spot: A-roads, B/C roads and unclassified roads are all declining
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (992km — 11% of network): declining</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">5.9%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">up from 3.8% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">31.1%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 24.2%</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">63.0%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 72.0%</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Even the main roads — SCANNER-surveyed every year and first in line for funding — have 
              worsened every single year since 2020. RED is up 55%, and good-condition A-roads have 
              fallen nine percentage points.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (2,334km — 26% of network): RED nearly doubled</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4.5%</td>
                    <td className="px-4 py-2 text-center">30.0%</td>
                    <td className="px-4 py-2 text-center">65.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">5.4%</td>
                    <td className="px-4 py-2 text-center">30.9%</td>
                    <td className="px-4 py-2 text-center">63.7%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5.9%</td>
                    <td className="px-4 py-2 text-center">30.2%</td>
                    <td className="px-4 py-2 text-center">63.9%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">6.3%</td>
                    <td className="px-4 py-2 text-center">31.8%</td>
                    <td className="px-4 py-2 text-center">61.9%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">8.4%</td>
                    <td className="px-4 py-2 text-center font-bold">33.1%</td>
                    <td className="px-4 py-2 text-center font-bold">58.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B/C roads have nearly doubled since 2020 (4.5% → 8.4%), with the biggest 
              single-year jump coming in 2024. More than four in ten B and C roads now need — or will 
              soon need — maintenance. And note the survey gap: C-roads are only SCANNER-surveyed 
              over a two-year cycle, 50% each year.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£54.3m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£79.3m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">75%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Kent tops up its DfT allocation with <span className="text-emerald-400">£25m of its own 
              capital</span> and has outspent its allocation every year since 2020/21 — and every road 
              class is still declining. By the council's own numbers, the network deteriorates faster 
              than a GREEN-rated spend programme can repair it.
            </p>
          </div>
        </div>
      </section>

      {/* The Unclassified Collapse */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 5,484km Problem
          </h2>
          <p className="text-surface-600 mb-8">
            62% of Kent's network is unclassified roads — and the share in RED condition more than 
            doubled in a single year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Unclassified roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16.8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18.0%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600 text-lg">39.0%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">From 18% to 39% in One Year</h3>
              <p className="text-surface-600 text-sm mb-3">
                Kent's own published figures show RED-rated unclassified roads more than doubled 
                between 2023 and 2024 — from 18% to 39%. On a 5,484km unclassified network, that is 
                roughly <strong>2,100km of residential streets, estate roads and rural lanes</strong> the 
                council's own data says should be considered for maintenance.
              </p>
              <p className="text-surface-600 text-sm">
                Whatever the cause — severe winters, the survey cycle catching up, or genuine collapse — 
                the council published this number itself. It cannot now argue it was unaware that the 
                road type making up 62% of its network is in widespread poor condition.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Drive-By Survey Gap</h3>
              <p className="text-amber-800 text-sm mb-3">
                Only unclassified roads classed as "Locally Important" get SCANNER laser surveys — and 
                even then only over a two-year cycle. The report states the remaining unclassified roads 
                "are subject to a coarse visual inspection (CVI) over a two-year period (50% each year)."
              </p>
              <p className="text-amber-800 text-sm">
                For most residential roads in Kent, the council's condition knowledge is up to two years 
                old and based on a visual assessment, not laser measurement. C-roads also only see a 
                SCANNER survey every other year.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for 
              knowing the condition of its roads and acting on it. For Kent's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last surveyed — and was it a SCANNER survey or a visual drive-by?</li>
              <li>• If 39% of unclassified roads were RED in 2024, what did the council do about yours?</li>
              <li>• Is a coarse visual inspection every two years a "reasonable" regime for a network the council knows is deteriorating?</li>
              <li>• Did inspection frequency increase after the council's own data showed RED roads doubling?</li>
            </ul>
            <p className="text-white font-medium">
              A council that publishes data showing 39% of its residential network in RED condition — 
              and inspects most of it visually, every other year — has a difficult story to tell about 
              reasonable care on those roads.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            233,509 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many potholes this network produces — roughly 
            128 filled every single day
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Patching (m²)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Public pothole reports</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">45,284</td>
                  <td className="px-6 py-4 text-center">508,424</td>
                  <td className="px-6 py-4 text-center">13,875</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">44,670</td>
                  <td className="px-6 py-4 text-center">482,998</td>
                  <td className="px-6 py-4 text-center">13,392</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">41,135</td>
                  <td className="px-6 py-4 text-center">390,605</td>
                  <td className="px-6 py-4 text-center">25,668</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">57,481</td>
                  <td className="px-6 py-4 text-center">289,163</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">32,146</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">44,939</td>
                  <td className="px-6 py-4 text-center">192,032</td>
                  <td className="px-6 py-4 text-center">24,217</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">233,509</td>
                  <td className="px-6 py-4 text-center font-bold">1,863,222</td>
                  <td className="px-6 py-4 text-center font-bold">109,298</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Reports Nearly Doubled</h3>
              <p className="text-surface-600 text-sm">
                Public pothole reports jumped from 13,392 in 2021/22 to 32,146 in 2023/24 — a 140% rise — 
                and were still 81% above the 2021/22 level in 2024/25. Every one of those reports is a 
                record of actual notice. If your pothole was reported before your incident and not fixed 
                in time, the Section 58 defence is in serious trouble.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Shrinking Patching Programme</h3>
              <p className="text-amber-800 text-sm">
                Patching output fell 62% over five years — from 508,424m² in 2020/21 to 192,032m² in 
                2024/25 — while pothole numbers stayed stubbornly around 45,000 a year and spiked to 
                57,481 in 2023/24. Less patching means more individual defects forming, exactly what 
                the public reporting figures show.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Backlog Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The £625m Admission
          </h2>
          <p className="text-surface-600 mb-8">
            Kent's own transparency report sets out the backlog, the shortfall and the winter damage — 
            in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="The road maintenance backlog in Kent was estimated in 2023/24 to be circa £625m, with an annual funding shortfall of over £30m to maintain roads in a steady state condition. The backlog and shortfall for all highway assets were estimated in 2023/24 to be circa £1.1bn and £93m respectively."
              source="Kent County Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard 
              quote="In addition, we experienced a couple of very severe winters which accelerated road deterioration in particular and led to a significant increase in road surface defects. Together, these factors, and not being able to fund steady state maintenance, had the effect of significantly increasing the maintenance backlog to over £1bn."
              source="Kent County Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard 
              quote="The additional capital funding announced by the government is being used to resource an extensive £14.2million Pothole Recovery Strategy intended to address the significant damage to our roads caused by winter weather in recent years."
              source="Kent County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What These Admissions Mean</h3>
              <p className="text-surface-600 text-sm mb-3">
                Kent formally acknowledges that it cannot fund steady-state maintenance, that severe 
                winters caused "a significant increase in road surface defects", and that its roads 
                backlog alone is circa £625m. That is documented, published knowledge of a network 
                producing defects faster than it can fix them.
              </p>
              <p className="text-surface-600 text-sm">
                Crucially, a funding shortfall is <strong>not</strong> a Section 58 defence. The 
                statutory test is whether the council took such care as was reasonably required to 
                secure the road was not dangerous — and courts do not excuse dangerous defects on 
                budget grounds. The backlog explains the potholes; it does not excuse them.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• If deterioration was known to be accelerating after severe winters, was inspection frequency increased on your road?</li>
                <li>• Was your road on the £14.2m Pothole Recovery Strategy list — and if so, why was the defect still there?</li>
                <li>• With a known £30m+ annual shortfall, how was your road prioritised — and is that prioritisation defensible?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Big-Spending AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Kent spends heavily and documents its asset management well — here's 
            how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — £79.3m capital spend against a £54.3m DfT allocation in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 75% of spend classed as preventative — 1.2 million m² of planned surfacing delivered in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management: published HAMP, revised Highway Safety Inspections Manual (May 2024), five-year forward works programme
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual SCANNER surveys on all A and B roads
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on main roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — every road class declining since 2020, with no exception
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 39% of unclassified roads RED in 2024 — more than double the year before
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Most unclassified roads inspected only by visual drive-by, every other year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Admitted £625m roads backlog and £30m+ annual shortfall — "not being able to fund steady state maintenance"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 233,509 potholes filled in five years — defects form faster than inspections catch them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — even the DfT says Kent's wider approach has room for improvement
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with a GREEN spend scorecard and a documented asset management plan, 
              your claim lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — Kent received 24,217 public pothole reports in 2024/25 alone, and any one matching yours is proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on an unclassified road, the two-yearly visual-only inspection regime and the 39% RED figure are your strongest structural arguments</li>
              <li>• Timing — incidents after the severe winters the council itself says caused "a significant increase in road surface defects" sit in a period of admitted, known deterioration</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, 
              and cites Kent's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Kent?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A big-spending council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No unclassified-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No backlog admission cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 39% RED unclassified-road figure documented</li>
                <li>✅ Two-yearly visual-only inspection regime argued</li>
                <li>✅ 233,509 potholes in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Kent</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=kent"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Kent Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Kent has a GREEN spend scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Kent spends well beyond its DfT allocation — £79.3m of capital against a £54.3m 
                allocation in 2025/26 — but the DfT still rates its road condition AMBER, and every road 
                class in Kent has declined since 2020. Section 58 turns on whether the specific defect 
                that damaged your vehicle was reasonably inspected and repaired, not on how much the 
                council spends in aggregate. A big budget spread across an 8,811km network does not fix 
                the pothole you hit.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does the jump to 39% RED unclassified roads mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 5,484km — 62% of Kent's network — and the council's own data 
                shows the RED share more than doubled in a single year, from 18% in 2023 to 39% in 2024. 
                That is roughly 2,100km of residential streets, estate roads and rural lanes the council 
                itself classes as needing consideration for maintenance. If your pothole was on one of 
                them, the council's own published figures put it on notice that this part of its network 
                is in widespread poor condition.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How often was my road actually surveyed?
              </h3>
              <p className="text-surface-600 text-sm">
                It depends on the class. Kent SCANNER-surveys all A and B roads every year, but C-roads 
                only over a two-year cycle (50% each year). On unclassified roads — most of the network — 
                only those classed as 'Locally Important' get SCANNER surveys over two years; the rest 
                receive a coarse visual inspection (CVI) over a two-year period. For most residential 
                roads, condition data could be up to two years old and based on a visual drive-by, not 
                laser measurement.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Kent's £625m backlog admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes, in two ways. First, it is documented knowledge: Kent's own transparency report 
                states the road maintenance backlog was estimated at circa £625m in 2023/24, with an 
                annual funding shortfall of over £30m just to keep roads in a steady state. The council 
                cannot claim to be surprised its network is producing defects. Second, lack of money is 
                not a Section 58 defence — the statutory test is whether the council took reasonable 
                care to secure the road was not dangerous, and courts do not excuse dangerous defects 
                on budget grounds.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Kent's £14.2m Pothole Recovery Strategy affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can help it. The council says the strategy is "intended to address the significant 
                damage to our roads caused by winter weather in recent years" — a written admission that 
                recent winters caused significant, known damage across the network. Knowledge of 
                accelerated deterioration raises the bar for what a reasonable inspection and repair 
                regime looks like in the affected period. If your incident falls in that window, the 
                council's own framing works in your favour.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How many potholes does Kent actually fill?
              </h3>
              <p className="text-surface-600 text-sm">
                A lot — and consistently. Kent filled 44,939 potholes in 2024/25 alone and 233,509 over 
                the five years to 2024/25, an average of roughly 128 every day. Public pothole reports 
                nearly doubled from 13,392 in 2021/22 to 32,146 in 2023/24. A network producing defects 
                at that rate is one where potholes routinely form between inspections — exactly the 
                scenario where prior reports and photographic evidence decide claims.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.kent.gov.uk/__data/assets/pdf_file/0019/216541/Local-highways-maintenance-transparency-report.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Kent County Council Local Highways Maintenance Transparency Report (June 2025)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
